import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import { useConfirm } from 'material-ui-confirm'
import { FunctionComponent, useEffect } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import { notSuccess } from '../../../../utils/notification'
import { swalClose, swalException, swalLoading } from '../../../../utils/swal'

import GrupoUnidadMedidaForm from '../GrUniMedForm'
import { GrupoUnidadMedidaActualizarValidationSchema } from '../../validator/GrupoUnidadMedidaActualizarValidationSchema'
import { grupoUnidadMedidaActualizarComposeService } from '../../services/grupoUnidadMedidaActualizarComposeService'
import { apiGrupoUnidadMedidaActualizar } from '../../api/apiGrupoUnidadMedidaActualizar'
import { useQueryClient } from '@tanstack/react-query'
import {
  GRUPO_UNIDAD_MEDIDA_INITIAL_VALUES,
  GrupoUnidadMedidaInputProps,
  GrupoUnidadMedidaProps,
} from '../../../../interfaces/grupoUnidadMedida.ts'

/**
 * Props del componente DialogActualizar
 * @typedef {Object} OwnProps
 * @property {boolean} keepMounted - Indica si el componente debe mantenerse montado cuando está cerrado
 * @property {boolean} open - Indica si el diálogo está abierto
 * @property {string} id - ID del grupo a actualizar (opcional, se puede obtener del objeto grupo)
 * @property {GrupoUnidadMedidaInputProps} grupo - Datos del grupo a actualizar
 * @property {Function} onClose - Función que se ejecuta al cerrar el diálogo
 */
interface OwnProps {
  keepMounted?: boolean
  open: boolean
  id?: string
  grupo: any // Cambiado a 'any' porque el objeto real de la API puede tener estructura diferente
  onClose: (value?: GrupoUnidadMedidaProps) => void
}

/**
 * Componente de diálogo para actualizar un Grupo de Unidad de Medida
 *
 * @param {OwnProps} props - Propiedades del componente
 * @returns {JSX.Element} Componente de diálogo
 */
const GrupoUnidadMedidaActualizarDialog: FunctionComponent<OwnProps> = ({
  onClose,
  open,
  grupo,
  ...other
}) => {
  // Inicialización del formulario con validación
  const form = useForm<GrupoUnidadMedidaInputProps>({
    defaultValues: GRUPO_UNIDAD_MEDIDA_INITIAL_VALUES,
    resolver: yupResolver(GrupoUnidadMedidaActualizarValidationSchema),
  })

  // Hook de confirmación para acciones importantes
  const confirm = useConfirm()

  /**
   * Efecto para actualizar el formulario cuando se abre el diálogo con datos existentes
   */
  useEffect(() => {
    if (grupo && open) {
      // Transformamos el objeto grupo al formato esperado por el formulario
      const valoresTransformados = grupoUnidadMedidaActualizarComposeService(grupo)

      // Aplicamos los valores al formulario
      form.reset(valoresTransformados)
    }
  }, [open, grupo, form])

  /**
   * Maneja el envío del formulario para actualizar el grupo
   * @param {GrupoUnidadMedidaInputProps} values - Valores del formulario
   */
  const onSubmit: SubmitHandler<GrupoUnidadMedidaInputProps> = async (values) => {
    try {
      // Validamos que exista un ID para actualizar
      const idGrupo = grupo?._id
      if (!idGrupo) {
        throw new Error('Error: No se pudo obtener el ID del grupo')
      }

      // Extraemos los datos necesarios para la actualización
      const { nombreGrupo, codigoUnidadMedidaBase } = values

      // Transformamos la definición eliminando campos que no necesitamos enviar a la API
      // pero preservando los datos esenciales
      const definicion = values.definicion.map(
        ({ codigoUnidadMedida, cantidadBase }) => ({
          codigoUnidadMedida,
          cantidadBase,
        }),
      )
      // Solicitar confirmación al usuario
      const { confirmed } = await confirm({
        description: '¿Estás seguro de que deseas actualizar este grupo?',
      })

      if (confirmed) {
        // Mostrar indicador de carga
        swalLoading()

        // Llamada a la API para actualizar el grupo
        const response = await apiGrupoUnidadMedidaActualizar(idGrupo, {
          nombreGrupo,
          codigoUnidadMedidaBase,
          definicion,
        })

        notSuccess('Actualización exitosa')
        onClose(response)
        swalClose()
        actualizarDatosGrupoUnidadMedida()
      }
    } catch (error) {
      console.error('Error en la actualización:', error)
      swalException(error)
    }
  }

  const queryClient = useQueryClient()

  const actualizarDatosGrupoUnidadMedida = () => {
    queryClient.invalidateQueries({
      queryKey: ['grupoUnidadMedidaListado'],
    })
  }

  return (
    <Dialog
      sx={{ '& .MuiDialog-paper': { width: '100%', maxWidth: 700 } }}
      maxWidth="xl"
      open={open}
      {...other}
    >
      <DialogTitle>Editar Grupo de Unidad de Medida</DialogTitle>
      <DialogContent dividers>
        <GrupoUnidadMedidaForm form={form} handleEdit={true} />
      </DialogContent>
      <DialogActions sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button autoFocus color="error" size="small" onClick={() => onClose()}>
          Cancelar
        </Button>
        <Button
          onClick={form.handleSubmit(onSubmit)}
          size="small"
          sx={{ mr: 2 }}
          variant="contained"
        >
          Actualizar
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default GrupoUnidadMedidaActualizarDialog
